import React, { useState, useEffect, useCallback } from 'react'
import { compose, map, filter, head, prop, toString, isEmpty, defaultTo } from 'ramda'
import { toast } from 'react-toastify'
import ListItemContainer from '../../../../../../components/ListItemContainer'
import InfoBar from '../../../../../../modules/InfoBar'
import Hint from '../../../../../../modules/Hint'
import Checkbox from '../../../../../../components/inputs/Checkbox'
import Button from '../../../../../../components/inputs/Button'
import FromSelect from '../../../../../../components/inputs/FromSelect'
import Divider from '../../../../../../components/Divider'
import FooterBar from '../../../../../../modules/FooterBar'
import Icon from '../../../../../../components/Icon'
import Wrapper from '../../../../../../components/Wrapper'
import { useFetch } from '../../../../../../hooks'
import FormAddHerdGrp from '../../../../components/FormAddHerdGrp'
import Modal from '../../../../../../components/Modal'

const CowsList = ({ grpid, infoBar, cowslist, sortCows, isManage, reFetchCard, translation }) => {
    const [isSelectAll, setIsSelectAll] = useState(false)
    const [cows, setCows] = useState([])
    const [mygrplist, setMygrplist] = useState([])
    const [selectedGroup, setSelectedGroup] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const { doFetch } = useFetch({
        url: `/rpc/move_cows_herdgrp`,
        method: 'POST',
        loader: false
    })

    const { doFetch: doFetchMygrplist } = useFetch({
        url: `/mygrplist`,
        method: 'GET',
        loader: false,
        state: false
    })

    const parseMygrplist = useCallback(compose(
        defaultTo([]),
        map(item => ({ value: item.grp_id, label: item.grp_name })),
        filter(item => item.grp_id !== grpid)
    ))

    const isDisabledSave = () => {
        return isEmpty(mygrplist) || !!!cows.find(cow => cow.checked) || isLoading;
    }

    useEffect(() => {
        if (grpid !== null) {
            (async () => {
                const res = await doFetchMygrplist();
                if (res) {
                    setMygrplist(parseMygrplist(res))
                }
            })()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grpid])

    useEffect(() => {
        if (cowslist) {
            setCows(cowslist.cows.map(cow => ({ ...cow, checked: false })))
        }
    }, [cowslist])

    useEffect(() => {
        if (mygrplist) {
            const getFirstValue = compose(
                toString,
                prop('value'),
                head
            )
            setSelectedGroup(getFirstValue(mygrplist))
        }
    }, [mygrplist])

    useEffect(() => {
        if (!isManage) {
            setCows(prevCowslistParsed => prevCowslistParsed.map(cow => ({ ...cow, checked: false })))
        }
    }, [isManage])

    useEffect(() => {
        if (!!cows.find(cow => !cow.checked)) {
            setIsSelectAll(false)
        } else {
            setIsSelectAll(true)
        }
    }, [cows])

    const handleClickCheckbox = event => {
        event.persist()
        setCows(prevCowslistParsed => prevCowslistParsed.map(cow => parseInt(cow.row_id, 10) === parseInt(event.target.name, 10) ? ({ ...cow, checked: event.target.checked }) : cow))
    }

    const handleClickSelectAll = () => {
        setCows(prevCowslistParsed => prevCowslistParsed.map(cow => ({ ...cow, checked: !isSelectAll })))
    }

    const handleChangeGroup = event => setSelectedGroup(event.target.value)

    const handleClickSave = () => {
        const parseCows = compose(
            map(cow => cow.row_id),
            filter(cow => cow.checked)
        );
        (async () => {
            setIsLoading(true)
            const res = await doFetch({
                data: {
                    cows: `{${parseCows(cows).toString()}}`,
                    grpid_from: grpid,
                    grpid_to: parseInt(selectedGroup, 10)
                }
            })
            if (res && res > 0) {
                reFetchCard({ 
                    loader: false, 
                    cbAfterFirstRequest: () => {
                        setIsLoading(false)
                        toast.success(translation && translation.footer)
                    }
                })
            }
        })();
    }

    return (
        <>
            {
                isManage ?
                    <Wrapper
                        noMargin
                    >
                        <Hint
                            copy={translation && translation.info}
                        />
                        {
                            isEmpty(mygrplist) ?
                                <Modal
                                    renderOpenButton={
                                        ({ handleToggleIsOpenModal }) => (
                                            <Icon onClick={handleToggleIsOpenModal} name='add' pointer />
                                        )
                                    }
                                >
                                    {
                                        ({ handleToggleIsOpenModal }) => (
                                            <FormAddHerdGrp
                                                onClickCancel={handleToggleIsOpenModal}
                                                onClose={doFetchMygrplist}
                                                translation={translation && translation['grpmodal']}
                                            />
                                        )
                                    }
                                </Modal>
                                :
                                <FromSelect
                                    name='group'
                                    onChange={handleChangeGroup}
                                    value={selectedGroup}
                                    options={mygrplist}
                                    maxWidth='200px'
                                    noMargin
                                />
                        }
                    </Wrapper>
                    :
                    infoBar &&
                    <InfoBar
                        lastUpdate={infoBar.lastUpdate}
                        translation={infoBar.translation}
                        noContainer
                    />
            }
            <ListItemContainer
                data={sortCows(cows)}
                isEditMode={isManage}
                editContent={item => (
                    <>
                        <Checkbox
                            name={item.row_id}
                            checked={item.checked}
                            noMargin
                            onClick={handleClickCheckbox}
                            disabled={isLoading}
                        />
                        <Divider vertical />
                    </>
                )}
            />
            {
                isManage &&
                <FooterBar>
                    <Button onClick={handleClickSelectAll} transparent noMargin fullWidth>
                        <Icon name={isSelectAll ? 'unselect_all' : 'select_all'} center pointer />
                    </Button>
                    <Button onClick={handleClickSave} transparent noMargin fullWidth disabled={isDisabledSave()}>
                        {translation && translation['buttons']['save']}
                    </Button>
                </FooterBar>
            }
        </>
    )
}

export default CowsList
